import { View, StyleSheet } from "react-native";
import { colors, layout } from "@/constants";
import ChatTextInput from "./ChatTextInput";
import ControlButton from "./base/ControlButton";
import TextButton from "./base/TextButton";
import { ConnectionState } from "./ChatRoom";

/**
 * Props for the ChatInputPanel component
 */
interface ChatInputPanelProps {
  /** Current text inputText in the input field */
  inputText: string;
  /** Placeholder text to show when input is empty */
  placeholderText?: string;
  /** Current connection state of the voice chat */
  connectionState: ConnectionState;
  /** Whether the microphone is currently enabled */
  isMicEnabled: boolean;
  /** Whether the speaker is currently enabled */
  isSpeakerEnabled: boolean;
  /** Callback function when connect button is pressed */
  onConnect: () => void;
  /** Callback function when disconnect button is pressed */
  onDisconnect: () => void;
  /** Callback function when microphone button is pressed */
  onToggleMic: () => void;
  /** Callback function when speaker button is pressed */
  onToggleSpeaker: () => void;
  /** Callback function when text changes */
  onChangeText: (text: string) => void;
  /** Callback function when send button is pressed or return key is pressed */
  onSend: () => void;
}

/**
 * Renders a cohesive chat input panel that handles both connection state and voice controls.
 * Shows a connect button when disconnected, and voice controls + text input when connected.
 *
 * @param props - The component props
 * @returns A unified input panel with connection and voice controls
 */
export default function ChatInputPanel({
  inputText,
  onChangeText,
  onSend,
  placeholderText = "What's on your mind?",
  connectionState,
  isMicEnabled,
  isSpeakerEnabled,
  onConnect,
  onDisconnect,
  onToggleMic,
  onToggleSpeaker
}: ChatInputPanelProps) {
  return (
    <View style={styles.container}>
      {connectionState === "disconnected" ? (
        // When disconnected, show connect interface
        <View style={styles.connectSection}>
          <TextButton
            buttonText="Chat"
            icon="graphic_eq"
            iconSize="large"
            width={120}
            height={56}
            onPress={onConnect}
            accessibilityLabel="Connect to voice chat"
          />
        </View>
      ) : (
        // When connected, show voice controls and text input
        <>
          <View style={styles.voiceControlsSection}>
            <ControlButton
              icon={isMicEnabled ? "mic" : "mic_off"}
              isActive={isMicEnabled}
              onPress={onToggleMic}
              accessibilityLabel={`Microphone ${isMicEnabled ? "enabled" : "disabled"}`}
            />

            <ControlButton
              icon={isSpeakerEnabled ? "volume_up" : "volume_off"}
              isActive={isSpeakerEnabled}
              onPress={onToggleSpeaker}
              accessibilityLabel={`Speaker ${isSpeakerEnabled ? "enabled" : "disabled"}`}
            />

            <ControlButton
              icon="close"
              isActive={false}
              onPress={onDisconnect}
              accessibilityLabel="Disconnect from voice chat"
            />
          </View>
          <View style={styles.inputSection}>
            <ChatTextInput
              inputText={inputText}
              onChangeText={onChangeText}
              placeholderText={placeholderText}
              onSubmitEditing={onSend}
            />

            <ControlButton
              icon="send"
              size={44}
              isActive={inputText.trim().length > 0}
              disabled={!inputText.trim()}
              onPress={onSend}
              accessibilityLabel="Send message"
            />
          </View>
        </>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: colors.surface,
    padding: layout.space.lg
  },
  connectSection: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center"
  },
  voiceControlsSection: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    gap: layout.space.md,
    marginBottom: layout.space.md
  },
  inputSection: {
    flexDirection: "row",
    alignItems: "flex-end",
    gap: layout.space.md
  }
});
